/**
 * Suche in Open Food Facts nach Text, zum Beispiel „Haferflocken“, wenn kein
 * Strichcode zur Hand ist. Ein Treffer wird wie beim Scannen geprüft und
 * übernommen.
 */
import { formatDecimal } from '@cookbook/domain';
import { useState } from 'react';
import { ApiError, api, isOffline } from '../../api/client.ts';
import type { FoundProduct, Product } from '../../api/types.ts';
import { texts } from '../../texts/de.ts';
import { FoundProductCard } from './FoundProductCard.tsx';

interface SourceSearchProps {
  readonly onAdopted: (product: Product) => void;
}

/** Suche mit Trefferliste. */
export function SourceSearch({ onAdopted }: SourceSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FoundProduct[] | null>(null);
  const [selected, setSelected] = useState<FoundProduct | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function search() {
    setBusy(true);
    setMessage(null);
    setSelected(null);
    try {
      const result = await api.get<{ found: FoundProduct[]; enabled: boolean }>(
        `/api/sources/search?q=${encodeURIComponent(query.trim())}`,
      );
      setResults(result.found);
      if (!result.enabled) {
        setMessage(texts.scan.searchDisabled);
      }
    } catch (caught) {
      setMessage(
        isOffline(caught)
          ? texts.scan.offline
          : caught instanceof ApiError
            ? caught.message
            : texts.common.error,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void search();
        }}
      >
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={texts.scan.searchPlaceholder}
          aria-label={texts.scan.searchSources}
          className="flex-1 rounded-xl border border-border-subtle bg-surface-raised px-3 py-2.5"
        />
        <button
          type="submit"
          disabled={busy || query.trim().length < 3}
          className="rounded-xl border border-border-subtle px-4 text-sm disabled:opacity-50"
        >
          {busy ? texts.scan.searchingShort : texts.scan.searchSources}
        </button>
      </form>
      {message === null ? null : <p className="text-sm text-content-muted">{message}</p>}
      {selected !== null ? (
        <div className="space-y-2">
          <button type="button" onClick={() => setSelected(null)} className="text-sm text-content-muted">
            ← {texts.scan.backToResults}
          </button>
          <FoundProductCard
            found={selected}
            actionLabel={texts.scan.adopt}
            onAdopted={(product) => onAdopted(product)}
          />
        </div>
      ) : results === null ? null : results.length === 0 ? (
        <p className="text-sm text-content-muted">{texts.scan.noResults}</p>
      ) : (
        <ul className="divide-y divide-border-subtle rounded-card border border-border-subtle bg-surface-raised">
          {results.map((found) => (
            <li key={`${found.source}-${found.sourceId}`}>
              <button
                type="button"
                onClick={() => setSelected(found)}
                className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm"
              >
                {found.imageUrl === null ? (
                  <span className="h-10 w-10 shrink-0 rounded-lg bg-surface-sunken" />
                ) : (
                  <img
                    src={found.imageUrl}
                    alt=""
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    className="h-10 w-10 shrink-0 rounded-lg bg-white object-contain"
                  />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{found.name}</span>
                  <span className="block truncate text-xs text-content-muted">
                    {[
                      found.brand,
                      found.packageAmount === null
                        ? found.quantityText
                        : `${formatDecimal(found.packageAmount)} ${found.packageUnit}`,
                    ]
                      .filter((part) => part)
                      .join(' · ')}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-content-muted">{texts.scan.searchNote}</p>
    </div>
  );
}
